import { Dialog as RD } from 'radix-ui';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ModalProps = {
  open: boolean; onOpenChange: (open: boolean) => void; title: string; description?: string;
  children: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg'; className?: string;
};

const WIDTH = { sm: 'max-w-[420px]', md: 'max-w-[560px]', lg: 'max-w-[640px]' } as const;

/** Header, body, footer bar - the shape every dialog in the mock-ups takes (6c). Radix handles focus, Esc and aria. */
export function Dialog({ open, onOpenChange, title, description, children, footer, size = 'md', className }: ModalProps) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 bg-[rgba(23,23,26,.18)]" />
        <RD.Content className={cn('fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-h-[calc(100dvh-64px)] overflow-y-auto rounded-auth border border-border bg-card shadow-[0_20px_60px_rgba(0,0,0,.18)] outline-none', WIDTH[size], className)}>
          <div className="pt-[22px] px-[26px]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <RD.Title className="text-[19px] font-extrabold tracking-[-.02em] m-0">{title}</RD.Title>
                {description && <RD.Description className="text-[13px] font-semibold text-ink-2 m-0 mt-[3px]">{description}</RD.Description>}
              </div>
              <RD.Close className="bg-transparent border-0 p-0 text-[20px] leading-none text-ink-2 hover:text-ink cursor-pointer" aria-label="Close"><X size={18} /></RD.Close>
            </div>
          </div>
          <div className="px-[26px] pt-5 pb-[26px]">{children}</div>
          {footer && <div className="flex justify-end gap-[10px] px-[26px] py-[18px] border-t border-header-rule bg-canvas">{footer}</div>}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
